import { CheckCircle, X, AlertCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { TagChip } from "@/components/ui/tag-chip";
import { useSubscriptionDisplay } from "@/hooks/useSubscriptionDisplay";
import { usePerformanceMeasure } from "@/perf/usePerformanceMeasure";
import { type Subscription } from "@/types/subscription";

interface SubscriptionHeaderProps {
  subscription: Subscription;
  tagObjects: { id: string; name: string; color?: string }[];
}

export const SubscriptionHeader = ({
  subscription,
  tagObjects,
}: SubscriptionHeaderProps) => {
  // Measure render time for SubscriptionHeader
  usePerformanceMeasure("SubscriptionHeader");

  const { getStatusVariant, getReconciliationIcon, getCategoryIcon } =
    useSubscriptionDisplay(subscription);

  const iconName = getReconciliationIcon();
  const IconComponent =
    iconName === "Check"
      ? CheckCircle
      : iconName === "X"
        ? X
        : iconName === "AlertCircle"
          ? AlertCircle
          : AlertCircle;

  return (
    <div className="mb-4 flex items-start justify-between">
      <div className="flex items-center space-x-3">
        <div className="text-2xl">{getCategoryIcon()}</div>
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold text-card-foreground">
              {subscription.name}
            </h3>
            {(subscription.billingType || "recurring") === "pay_as_you_go" && (
              <Badge className="bg-purple-100 px-1.5 py-0 text-xs text-purple-800 dark:bg-purple-900 dark:text-purple-100">
                PAYG
              </Badge>
            )}
          </div>
          {tagObjects.length === 0 && (
            <p className="text-sm text-muted-foreground">
              {subscription.category}
            </p>
          )}
          {tagObjects.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">
              {tagObjects.map((tag) => (
                <TagChip key={tag.id} label={tag.name} color={tag.color} />
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center space-x-2">
        <Badge variant={getStatusVariant()}>
          {subscription.status.charAt(0).toUpperCase() +
            subscription.status.slice(1)}
        </Badge>
        <div className="flex items-center">
          <IconComponent
            className={`h-4 w-4 ${
              iconName === "Check"
                ? "text-success"
                : iconName === "X"
                  ? "text-destructive"
                  : iconName === "AlertCircle"
                    ? "text-warning"
                    : "text-muted-foreground"
            }`}
          />
        </div>
      </div>
    </div>
  );
};
